import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterLink } from "@angular/router";
import { AuthService } from "src/app/security/auth.service";
import { Menu } from "src/app/shared/model/security/menu.model";
import { rutaDeMenu } from "src/app/shared/menu/ruta-menu";

/** Una tarjeta de la página: una hoja del menú con su texto de ayuda. */
interface Tarjeta {
  titulo: string;
  descripcion: string;
  icono: string;
  ruta: string[];
}

interface Seccion {
  titulo: string;
  tarjetas: Tarjeta[];
}

/**
 * Configuración: una sola entrada en el menú y adentro, por secciones, todo
 * lo que se ajusta una vez y se deja andando.
 *
 * NO TIENE PERMISOS PROPIOS. Cada tarjeta es una hoja del menú que el
 * usuario ya tiene (las hijas del grupo «Configuración» que arma el
 * servidor); si no tiene la hoja, no ve la tarjeta. Así los permisos siguen
 * viviendo en un solo lugar y esta página no puede enseñar de más.
 *
 * Los textos de ayuda se curan aquí por factory —el nombre del menú solo
 * dice qué es, no para qué sirve—; una hoja nueva sin texto cae en «Otros»
 * con su nombre, para que nunca quede escondida.
 */
@Component({
  selector: "app-configuracion",
  standalone: true,
  templateUrl: "./configuracion.component.html",
  styleUrls: ["./configuracion.component.css"],
  imports: [CommonModule, RouterLink],
})
export class ConfiguracionComponent implements OnInit {
  secciones: Seccion[] = [];

  /** factory de la hoja → dónde va y qué dice. */
  private static readonly CATALOGO: Record<string, { seccion: string; descripcion: string; icono: string }> = {
    "admin/areas": { seccion: "Organización", descripcion: "Áreas y puestos con los que se clasifica al personal.", icono: "account_tree" },
    "admin/configgeneral": { seccion: "Organización", descripcion: "Parámetros generales de la empresa.", icono: "tune" },
    "admin/perfil": { seccion: "Accesos", descripcion: "Qué pantallas ve cada perfil de usuario.", icono: "admin_panel_settings" },
    "planes": { seccion: "Accesos", descripcion: "Módulos contratados, cupo de personas y vigencia.", icono: "workspace_premium" },
    "admin/mantenimiento-modulos": { seccion: "Accesos", descripcion: "Pon pantallas y funciones en mantenimiento: quien entra ve un aviso.", icono: "construction" },
    "asistencia/parametros": { seccion: "Asistencia", descripcion: "Zona horaria, regla de falta, tolerancias y hora de corte.", icono: "schedule" },
    "asistencia/ubicaciones": { seccion: "Asistencia", descripcion: "Sitios propios y de cliente, con geocerca y canal de registro.", icono: "location_on" },
    "asistencia/estaciones": { seccion: "Asistencia", descripcion: "Kioscos de huella y sus credenciales.", icono: "point_of_sale" },
    "admin/inventario-ia/catalogos": { seccion: "IA", descripcion: "Catálogos del inventario de sistemas de IA.", icono: "category" },
  };

  private static readonly ORDEN = ["Organización", "Accesos", "Asistencia", "IA", "Otros"];

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.secciones = this.armar(this.authService.getMenuList() || []);
  }

  get vacia(): boolean {
    return this.secciones.length === 0;
  }

  private armar(menu: Menu[]): Seccion[] {
    const porSeccion = new Map<string, Tarjeta[]>();
    for (const raiz of menu) {
      // Por factory, no por nombre: el grupo se puede renombrar desde la
      // gestión del menú y su factory ('configuracion') no cambia. Las diagonales
      // de los extremos se quitan sin retroceder de más: la cola arranca tras un
      // carácter que no es diagonal y $1 lo devuelve.
      const factory = (raiz.factory || "").trim().replace(/^\/+|([^/])\/+$/g, "$1").toLowerCase();
      if (factory !== "configuracion" && (raiz.nombre || "").trim().toLowerCase() !== "configuración") {
        continue;
      }
      for (const hoja of raiz.hijos || []) {
        const llave = (hoja.factory || "").trim().replace(/^\/+/, "");
        const info = ConfiguracionComponent.CATALOGO[llave];
        const seccion = info?.seccion ?? "Otros";
        const lista = porSeccion.get(seccion) ?? [];
        lista.push({
          titulo: hoja.nombre,
          descripcion: info?.descripcion ?? "",
          icono: info?.icono ?? hoja.icono ?? "settings",
          ruta: rutaDeMenu(hoja, raiz),
        });
        porSeccion.set(seccion, lista);
      }
    }
    return ConfiguracionComponent.ORDEN
      .filter((titulo) => porSeccion.has(titulo))
      .map((titulo) => ({ titulo, tarjetas: porSeccion.get(titulo) ?? [] }));
  }
}
